/**
 * The languages the public site speaks, and how each appears in a URL.
 *
 * English is the default and carries no prefix: `/services`. Every other
 * language is a prefix on the same path: `/tl/services`, `/cb/services`.
 * The segment is what a person types and shares; the code is what the
 * translation files and `<html lang>` use (Cebuano's ISO code is `ceb`,
 * but `/cb` is what the URL was asked to be).
 *
 * Each language also names the currency its speakers most likely think in.
 * It is a default, not a rule: the currency picker overrides it, and only a
 * currency with a rate on file is ever shown.
 */
export const LOCALES = [
  { code: 'en', segment: '', name: 'English', native: 'English', currency: null, htmlLang: 'en' },
  { code: 'tl', segment: 'tl', name: 'Tagalog', native: 'Tagalog', currency: 'PHP', htmlLang: 'tl' },
  { code: 'ceb', segment: 'cb', name: 'Cebuano', native: 'Cebuano', currency: 'PHP', htmlLang: 'ceb' },
  { code: 'th', segment: 'th', name: 'Thai', native: 'ไทย', currency: 'THB', htmlLang: 'th' },
  { code: 'vi', segment: 'vi', name: 'Vietnamese', native: 'Tiếng Việt', currency: 'VND', htmlLang: 'vi' },
]

export const DEFAULT_LOCALE = 'en'

/** The regex fragment the router uses for the optional prefix. */
export const LOCALE_SEGMENT_PATTERN = LOCALES.filter((l) => l.segment)
  .map((l) => l.segment)
  .join('|')

export const localeBySegment = (segment) =>
  LOCALES.find((l) => l.segment === String(segment ?? '')) ?? LOCALES[0]

export const localeByCode = (code) => LOCALES.find((l) => l.code === String(code ?? '')) ?? LOCALES[0]

/**
 * The language the browser asks for, if it is one we speak. Filipino
 * ("fil") is treated as Tagalog, which is what most devices in the
 * Philippines report.
 */
export function detectLocale(languages = navigator.languages ?? [navigator.language]) {
  for (const tag of languages) {
    const base = String(tag ?? '')
      .toLowerCase()
      .split('-')[0]
    if (base === 'fil' || base === 'tl') return 'tl'
    if (base === 'ceb') return 'ceb'
    if (base === 'th') return 'th'
    if (base === 'vi') return 'vi'
    if (base === 'en') return 'en'
  }
  return DEFAULT_LOCALE
}

/** `/tl/services` for `('tl', '/services')`; `/services` for English. */
export function localisePath(code, path) {
  const locale = localeByCode(code)
  const clean = String(path ?? '/').replace(new RegExp(`^/(?:${LOCALE_SEGMENT_PATTERN})(?=/|$)`), '') || '/'
  return locale.segment ? `/${locale.segment}${clean === '/' ? '' : clean}` || `/${locale.segment}` : clean
}
